import React, {useCallback, useEffect, useRef, useState} from 'react';

/**
 * Reusable index page table: search, sortable columns, select filters,
 * pagination and row actions. Data comes from `fetcher(params)`, which must
 * resolve to {data: [], meta: {total, page, last_page}, filters?: {}}.
 *
 * columns: [{key, label, sortable, render(row), className}]
 * filters: [{key, label, options: [{value, label}] | (response) => options}]
 *          or [{key, label, type: 'number', width}] for free-number filters
 * params: extra fixed params from the page (e.g. an artist picked elsewhere);
 *         changing them resets to page 1.
 * actions: (row, reload) => ReactNode
 */
export default function DataTable({fetcher, columns, filters = [], actions, searchPlaceholder = 'Search…',
    defaultSort = {key: 'created_at', dir: 'desc'}, perPage = 25, params: extra = {}, onRowClick}) {
    const [params, setParams] = useState({q: '', sort: defaultSort.key, dir: defaultSort.dir, page: 1});
    const extraKey = JSON.stringify(extra);
    useEffect(() => { setParams((p) => ({...p, page: 1})); }, [extraKey]);
    const [response, setResponse] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const seq = useRef(0);
    const typing = useRef(null);
    const [q, setQ] = useState('');

    const load = useCallback(() => {
        const n = ++seq.current;
        setLoading(true);
        fetcher({...params, ...extra, per_page: perPage})
            .then((r) => { if (n === seq.current) { setResponse(r); setError(null); } })
            .catch((e) => { if (n === seq.current) setError(e.message); })
            .finally(() => { if (n === seq.current) setLoading(false); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetcher, params, perPage, extraKey]);

    useEffect(() => { load(); }, [load]);

    const set = (patch) => setParams((p) => ({...p, page: 1, ...patch}));
    const onSearch = (v) => {
        setQ(v);
        clearTimeout(typing.current);
        typing.current = setTimeout(() => set({q: v}), 250);
    };
    const toggleSort = (key) => set(params.sort === key
        ? {dir: params.dir === 'asc' ? 'desc' : 'asc'}
        : {sort: key, dir: 'asc'});

    const rows = response?.data || [];
    const meta = response?.meta;

    return (
        <div>
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex min-w-[240px] flex-1 items-center gap-3 rounded-xl bg-white/[0.07] px-3.5 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]">
                    <i className="fa-solid fa-magnifying-glass text-[12px] text-white/40"/>
                    <input value={q} onChange={(e) => onSearch(e.target.value)} placeholder={searchPlaceholder}
                           className="w-full bg-transparent text-[13px] text-white placeholder-white/30 outline-none"/>
                </div>
                {filters.map((f) => {
                    if (f.type === 'number') {
                        return (
                            <input key={f.key} type="number" placeholder={f.label} defaultValue={params[f.key] ?? ''}
                                   onChange={(e) => { const v = e.target.value; clearTimeout(typing.current); typing.current = setTimeout(() => set({[f.key]: v}), 400); }}
                                   style={{width: f.width || 110}}
                                   className="rounded-xl bg-white/[0.07] px-3 py-2.5 text-[13px] text-white/80 placeholder-white/35 outline-none"/>
                        );
                    }
                    const options = typeof f.options === 'function' ? f.options(response) : f.options;
                    return (
                        <select key={f.key} value={params[f.key] ?? ''} onChange={(e) => set({[f.key]: e.target.value})}
                                className="rounded-xl bg-white/[0.07] px-3 py-2.5 text-[13px] text-white/80 outline-none">
                            <option value="">{f.label}: all</option>
                            {(options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    );
                })}
            </div>

            {error && <p className="mt-4 text-[13px] text-apple-red">{error}</p>}

            <div className="relative mt-4 overflow-x-auto rounded-2xl bg-white/[0.04] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                {loading && (
                    <div className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center rounded-2xl bg-ambient-base/40 pt-24">
                        <span className="flex items-center gap-2.5 rounded-full bg-white/[0.10] px-4 py-2 text-[12.5px] font-semibold text-white/80 shadow-[0_18px_44px_rgba(0,0,0,0.45)] backdrop-blur-xl">
                            <i className="fa-solid fa-circle-notch fa-spin text-apple-red"/>Loading…
                        </span>
                    </div>
                )}
                <table className={'w-full text-left text-[13px] transition-opacity ' + (loading && response ? 'opacity-40' : '')}>
                    <thead>
                        <tr className="text-[10.5px] uppercase tracking-[0.08em] text-white/40">
                            {columns.map((c) => (
                                <th key={c.key} className={'px-4 py-3 font-bold ' + (c.className || '')}>
                                    {c.sortable ? (
                                        <button type="button" onClick={() => toggleSort(c.key)} className="flex items-center gap-1.5 hover:text-white">
                                            {c.label}
                                            <i className={'fa-solid text-[9px] ' + (params.sort === c.key
                                                ? (params.dir === 'asc' ? 'fa-arrow-up text-apple-red' : 'fa-arrow-down text-apple-red')
                                                : 'fa-sort opacity-40')}/>
                                        </button>
                                    ) : c.label}
                                </th>
                            ))}
                            {actions && <th className="px-4 py-3"/>}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.id} onClick={onRowClick ? () => onRowClick(row) : undefined}
                                className={'border-t border-white/[0.06] transition hover:bg-white/[0.03] ' + (onRowClick ? 'cursor-pointer' : '')}>
                                {columns.map((c) => (
                                    <td key={c.key} className={'px-4 py-3 ' + (c.className || '')}>
                                        {c.render ? c.render(row) : (row[c.key] ?? '—')}
                                    </td>
                                ))}
                                {actions && <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>{actions(row, load)}</td>}
                            </tr>
                        ))}
                        {!response && (
                            <tr><td colSpan={columns.length + 1} className="h-64"/></tr>
                        )}
                        {response && rows.length === 0 && (
                            <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-white/40">Nothing here.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {meta && (
                <div className="mt-4 flex items-center justify-end gap-3 text-[12px] text-white/50">
                    <span>{meta.total.toLocaleString()}{meta.total_capped ? '+' : ''} total</span>
                    <button type="button" disabled={meta.page <= 1} onClick={() => setParams((p) => ({...p, page: p.page - 1}))}
                            className="rounded-lg bg-white/[0.07] px-3 py-1.5 disabled:opacity-30">Prev</button>
                    <span>{meta.page} / {meta.last_page}</span>
                    <button type="button" disabled={meta.page >= meta.last_page} onClick={() => setParams((p) => ({...p, page: p.page + 1}))}
                            className="rounded-lg bg-white/[0.07] px-3 py-1.5 disabled:opacity-30">Next</button>
                </div>
            )}
        </div>
    );
}
